import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { CompetitionEntry } from '../types';
import { queryCache, CACHE_TTL } from '../lib/cache';
import { handleError } from '../lib/errorHandler';

interface UseCompetitionEntriesOptions {
  competitionId?: string;
  userId?: string;
}

export const useCompetitionEntries = (options: UseCompetitionEntriesOptions = {}) => {
  const [entries, setEntries] = useState<CompetitionEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<CompetitionEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const isMountedRef = useRef(true);
  const hasAutoSelectedRef = useRef(false);

  const userId = options.userId || user?.id;
  const { competitionId } = options;

  const fetchEntries = useCallback(async (forceRefresh: boolean = false) => {
    // Don't fetch if no user or no competitionId - prevents fetching ALL entries across competitions
    if (!userId || !competitionId) {
      setEntries([]);
      setLoading(false);
      return;
    }

    const cacheKey = queryCache.generateKey('competitionEntries', {
      userId,
      competitionId
    });

    try {
      setLoading(true);
      setError(null);

      // Invalidate cache if forcing refresh to get fresh data (e.g., after payment or pick)
      if (forceRefresh) {
        queryCache.invalidate(cacheKey);
      }

      const result = await queryCache.deduplicate(
        cacheKey,
        async () => {
          const { data, error } = await supabase
            .from('competition_entries')
            .select('id, user_id, competition_id, entry_nickname, entry_number, payment_status, deleted_at, created_at, updated_at')
            .eq('user_id', userId)
            .eq('competition_id', competitionId)
            .is('deleted_at', null)
            .order('entry_number', { ascending: true });

          if (error) {
            throw error;
          }

          const formattedEntries: CompetitionEntry[] = (data || []).map(row => ({
            id: row.id,
            userId: row.user_id,
            competitionId: row.competition_id,
            entryNickname: row.entry_nickname,
            entryNumber: row.entry_number,
            paymentStatus: row.payment_status,
            createdAt: new Date(row.created_at),
            updatedAt: new Date(row.updated_at),
            deletedAt: row.deleted_at ? new Date(row.deleted_at) : undefined
          }));

          return formattedEntries;
        },
        CACHE_TTL.SHORT
      );

      if (isMountedRef.current) {
        setEntries(result);
        // Keep selectedEntry in sync so payment_status updates after refresh/payment.
        setSelectedEntry((prev) => {
          if (!prev) return prev;
          return result.find((e) => e.id === prev.id) ?? prev;
        });
      }
    } catch (err) {
      console.error('[useCompetitionEntries] Error fetching entries:', err);
      handleError(err as Error, {
        hook: 'useCompetitionEntries',
        action: 'fetchEntries',
        metadata: { userId, competitionId }
      });
      if (isMountedRef.current) {
        setError('Failed to load competition entries');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [userId, competitionId]);

  // Auto-select first entry when entries load (separate from fetchEntries to avoid infinite loop)
  useEffect(() => {
    if (entries.length > 0 && !selectedEntry && !hasAutoSelectedRef.current) {
      hasAutoSelectedRef.current = true;
      const savedEntryId = competitionId ? localStorage.getItem(`selectedEntry_${competitionId}`) : null;
      const paidEntry = entries.find(
        (e) => e.paymentStatus === 'paid' || e.paymentStatus === 'free'
      );
      const entryToSelect = savedEntryId
        ? entries.find((e) => e.id === savedEntryId) || paidEntry || entries[0]
        : paidEntry || entries[0];
      setSelectedEntry(entryToSelect);
    }
  }, [entries, selectedEntry, competitionId]);

  const createEntry = async (entryNickname: string, competitionIdParam: string, isFreeCompetition: boolean = false) => {
    if (!userId) {
      throw new Error('User must be authenticated');
    }

    const trimmedNickname = entryNickname.trim();
    if (trimmedNickname.length < 2 || trimmedNickname.length > 50) {
      throw new Error('Entry nickname must be between 2 and 50 characters');
    }

    queryCache.invalidateByParams('competitionEntries', { userId });

    // Helper: hydrate a CompetitionEntry from a DB row
    const hydrateEntry = (row: {
      id: string;
      user_id: string;
      competition_id: string;
      entry_nickname: string;
      entry_number: number;
      payment_status: string;
      created_at: string;
      updated_at: string;
    }): CompetitionEntry => ({
      id: row.id,
      userId: row.user_id,
      competitionId: row.competition_id,
      entryNickname: row.entry_nickname,
      entryNumber: row.entry_number,
      paymentStatus: row.payment_status as CompetitionEntry['paymentStatus'],
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
    });

    // Auto-named "Entry N" creations are idempotent — if a row already exists
    // (e.g. from a previous session, race, or a stale local cache) reuse it
    // instead of throwing a unique-constraint error to the user.
    const isAutoNamed = /^Entry \d+$/i.test(trimmedNickname);

    try {
      // Check if nickname is already used
      const { data: existing } = await supabase
        .from('competition_entries')
        .select('id, user_id, competition_id, entry_nickname, entry_number, payment_status, created_at, updated_at')
        .eq('user_id', userId)
        .eq('competition_id', competitionIdParam)
        .eq('entry_nickname', trimmedNickname)
        .is('deleted_at', null)
        .maybeSingle();

      if (existing) {
        if (isAutoNamed) {
          const reused = hydrateEntry(existing);
          setEntries(prev => (prev.some(e => e.id === reused.id) ? prev : [...prev, reused]));
          return reused;
        }
        throw new Error('You already have an entry with this nickname');
      }

      // Check entry limit
      const { data: limitCheck, error: limitError } = await supabase
        .rpc('check_entry_limit', {
          p_user_id: userId,
          p_competition_id: competitionIdParam
        });

      if (limitError) {
        throw limitError;
      }

      if (!limitCheck) {
        throw new Error('You have reached the maximum number of entries for this competition');
      }

      // Get next entry number
      const { data: nextNumber, error: numberError } = await supabase
        .rpc('get_next_entry_number', {
          p_user_id: userId,
          p_competition_id: competitionIdParam
        });

      if (numberError) {
        throw numberError;
      }

      // Competition entry_fee_enabled is source of truth (client flag can race).
      const { data: competitionRow } = await supabase
        .from('competitions')
        .select('entry_fee_enabled')
        .eq('id', competitionIdParam)
        .maybeSingle();
      const feeEnabled =
        competitionRow?.entry_fee_enabled ?? !isFreeCompetition;
      const paymentStatus = feeEnabled ? 'pending' : 'free';
      const { data, error } = await supabase
        .from('competition_entries')
        .insert({
          user_id: userId,
          competition_id: competitionIdParam,
          entry_nickname: trimmedNickname,
          entry_number: nextNumber,
          payment_status: paymentStatus
        })
        .select('id, user_id, competition_id, entry_nickname, entry_number, payment_status, created_at, updated_at')
        .single();

      if (error) {
        // Race-safe recovery: another insert won the unique constraint between
        // our pre-check and the insert. For auto-named entries, fetch and reuse
        // the existing row instead of surfacing a confusing duplicate-key error.
        const isUniqueViolation =
          (error as { code?: string }).code === '23505' ||
          /duplicate key|unique constraint/i.test(error.message || '');
        if (isUniqueViolation && isAutoNamed) {
          const { data: existingRow } = await supabase
            .from('competition_entries')
            .select('id, user_id, competition_id, entry_nickname, entry_number, payment_status, created_at, updated_at')
            .eq('user_id', userId)
            .eq('competition_id', competitionIdParam)
            .eq('entry_nickname', trimmedNickname)
            .is('deleted_at', null)
            .maybeSingle();
          if (existingRow) {
            const reused = hydrateEntry(existingRow);
            setEntries(prev => (prev.some(e => e.id === reused.id) ? prev : [...prev, reused]));
            return reused;
          }
        }
        // Benign unique-constraint races are handled above. Other insert
        // failures surface to the caller.
        throw error;
      }

      const newEntry = hydrateEntry(data);

      setEntries(prev => [...prev, newEntry]);
      return newEntry;
    } catch (err) {
      handleError(err as Error, {
        hook: 'useCompetitionEntries',
        action: 'createEntry',
        metadata: { userId, competitionId: competitionIdParam }
      });
      throw err;
    }
  };

  const updateEntryNickname = async (entryId: string, newNickname: string, competitionIdForUpdate?: string): Promise<{ success: boolean; error?: string }> => {
    if (!userId) {
      return { success: false, error: 'User must be authenticated' };
    }

    const trimmedNickname = newNickname.trim();
    if (trimmedNickname.length < 2 || trimmedNickname.length > 50) {
      return { success: false, error: 'Nickname must be between 2 and 50 characters' };
    }

    // Try to find the entry in local state first, otherwise fetch from database
    let entryCompetitionId = entries.find(e => e.id === entryId)?.competitionId;
    
    // If not in local state, use the provided competitionId or fetch from database
    if (!entryCompetitionId) {
      if (competitionIdForUpdate) {
        entryCompetitionId = competitionIdForUpdate;
      } else {
        // Fetch the entry from database to get its competition ID
        const { data: entryData, error: fetchError } = await supabase
          .from('competition_entries')
          .select('competition_id')
          .eq('id', entryId)
          .eq('user_id', userId)
          .is('deleted_at', null)
          .maybeSingle();
        
        if (fetchError || !entryData) {
          return { success: false, error: 'Entry not found' };
        }
        entryCompetitionId = entryData.competition_id;
      }
    }

    try {
      // Check if nickname is already used by another entry in the same competition
      const { data: existing } = await supabase
        .from('competition_entries')
        .select('id')
        .eq('user_id', userId)
        .eq('competition_id', entryCompetitionId)
        .eq('entry_nickname', trimmedNickname)
        .neq('id', entryId)
        .is('deleted_at', null)
        .maybeSingle();

      if (existing) {
        return { success: false, error: 'You already have an entry with this nickname' };
      }

      // Update the entry
      const { error } = await supabase
        .from('competition_entries')
        .update({ entry_nickname: trimmedNickname, updated_at: new Date().toISOString() })
        .eq('id', entryId)
        .eq('user_id', userId);

      if (error) {
        throw error;
      }

      // Update local state if entry is in the current list
      setEntries(prev => prev.map(e => 
        e.id === entryId ? { ...e, entryNickname: trimmedNickname, updatedAt: new Date() } : e
      ));

      // Also update selectedEntry if it's the one being edited
      if (selectedEntry?.id === entryId) {
        setSelectedEntry(prev => prev ? { ...prev, entryNickname: trimmedNickname, updatedAt: new Date() } : null);
      }

      // Invalidate cache
      queryCache.invalidateByParams('competitionEntries', { userId });

      return { success: true };
    } catch (err) {
      console.error('[useCompetitionEntries] Error updating entry nickname:', err);
      handleError(err as Error, {
        hook: 'useCompetitionEntries',
        action: 'updateEntryNickname',
        metadata: { userId, entryId }
      });
      return { success: false, error: 'Failed to update entry nickname' };
    }
  };

  const selectEntry = useCallback((entry: CompetitionEntry) => {
    setSelectedEntry(entry);
    if (entry.competitionId) {
      localStorage.setItem(`selectedEntry_${entry.competitionId}`, entry.id);
    }
  }, []);

  const getEntryByCompetition = useCallback((compId: string): CompetitionEntry[] => {
    return entries.filter(e => e.competitionId === compId);
  }, [entries]);

  const canAddEntry = useCallback(async (compId: string): Promise<boolean> => {
    if (!userId) return false;

    try {
      const { data, error } = await supabase
        .rpc('check_entry_limit', {
          p_user_id: userId,
          p_competition_id: compId
        });

      if (error) {
        console.error('Error checking entry limit:', error);
        return false;
      }

      return data;
    } catch (err) {
      console.error('Error checking entry limit:', err);
      return false;
    }
  }, [userId]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Clear state immediately when competition changes to prevent stale data
    setEntries([]);
    setSelectedEntry(null);
    hasAutoSelectedRef.current = false; // Reset auto-selection flag when competition changes
    setLoading(true);

    fetchEntries();
  }, [fetchEntries]);

  return {
    entries,
    selectedEntry,
    loading,
    error,
    createEntry,
    selectEntry,
    getEntryByCompetition,
    canAddEntry,
    updateEntryNickname,
    refreshEntries: () => fetchEntries(true) // Force cache invalidation on manual refresh
  };
};
